import { useMemo, useState, type FormEvent } from "react";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/base/BackToTop";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import { useSeo } from "@/hooks/useSeo";
import { siteConfig } from "@/mocks/site";

const FORM_ENDPOINT = "https://readdy.ai/api/form/daihlrboh653ivfvodi0";

const topics = ["词条内容更正", "补充事件线索", "合作与授权咨询", "其他问题"];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formMessage, setFormMessage] = useState("");
  const [messageLength, setMessageLength] = useState(0);

  const jsonLd = useMemo(() => {
    const origin = window.location.origin;
    return [
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "联系我们 - 黑料百科",
        description: "通过联系表单提交词条更正、事件线索或合作咨询，我们将在两个工作日内回复。",
        inLanguage: "zh-CN",
        url: `${origin}/contact`,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${origin}/` },
          { "@type": "ListItem", position: 2, name: "联系我们", item: `${origin}/contact` },
        ],
      },
    ];
  }, []);

  useSeo({
    title: "联系我们 - 黑料百科词条更正、线索补充与商务合作",
    description:
      "联系黑料百科：提交词条内容更正、补充事件线索或进行合作咨询。请附上可查证的公开来源，我们核实后会在词条页标注修改时间。",
    keywords: "联系黑料百科,词条更正,事件线索,合作咨询,联系表单",
    path: "/contact",
    jsonLd,
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormMessage("提交成功，我们已收到你的说明。");
      form.reset();
      setMessageLength(0);
      return;
    }

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key === "contact_alt") return;
      const text = String(value).trim();
      if (text) payload.append(key, text);
    });

    setStatus("submitting");
    setFormMessage("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = serverMsg.toLowerCase().includes("spam");

      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setStatus("error");
        setFormMessage(
          serverMsg && !isSpam
            ? serverMsg
            : "提交失败，请检查填写内容后重试，或直接发送邮件联系我们。"
        );
        return;
      }

      setStatus("success");
      setFormMessage("提交成功，我们已收到你的说明，会在两个工作日内回复。");
      form.reset();
      setMessageLength(0);
    } catch {
      setStatus("error");
      setFormMessage("网络异常导致提交失败，请稍后重试，或直接发送邮件联系我们。");
    }
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full">
        <section className="w-full border-b border-background-200 bg-background-100 px-4 py-8 md:px-10 md:py-12">
          <Breadcrumbs items={[{ label: "首页", href: "/" }, { label: "联系我们" }]} />
          <div className="mt-5 max-w-3xl">
            <span className="font-heading text-sm font-bold tracking-[0.2em] text-primary-600">
              CONTACT
            </span>
            <h1 className="mt-3 font-heading text-3xl font-black leading-tight text-foreground-950 md:text-5xl">
              联系我们
            </h1>
            <p className="mt-4 text-base leading-relaxed text-foreground-600">
              无论是词条更正、线索补充还是合作咨询，请尽量附上可查证的公开来源。核实后我们会在词条页标注修改时间与内容范围。
            </p>
          </div>
        </section>

        <section className="w-full px-4 py-12 md:px-10 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
            <div className="lg:col-span-7">
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-8"
              >
                <h2 className="font-heading text-xl font-bold text-foreground-950">提交说明</h2>
                <p className="mt-2 text-sm text-foreground-600">
                  带 * 的为必填项。文字说明请控制在 500 字以内。
                </p>

                <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-800">
                      称呼 <span className="text-primary-600">*</span>
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      placeholder="怎么称呼你"
                      className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-800">
                      邮箱 <span className="text-primary-600">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder="用于接收回复"
                      className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                </div>

                <div className="mt-5">
                  <label htmlFor="topic" className="block text-sm font-medium text-foreground-800">
                    咨询类型 <span className="text-primary-600">*</span>
                  </label>
                  <select
                    id="topic"
                    name="topic"
                    required
                    defaultValue={topics[0]}
                    className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  >
                    {topics.map((topic) => (
                      <option key={topic} value={topic}>
                        {topic}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mt-5">
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-800">
                    详细说明 <span className="text-primary-600">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={6}
                    onChange={(e) => setMessageLength(e.target.value.length)}
                    placeholder="请描述需要更正或补充的内容，并附上可查证的来源链接"
                    className="mt-2 w-full resize-y rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                  <div className="mt-1.5 text-right text-xs text-foreground-500">
                    {messageLength} / 500
                  </div>
                </div>

                <div className="intake-note-field" aria-hidden="true">
                  <label htmlFor="contact_alt">备注（请勿填写）</label>
                  <input
                    id="contact_alt"
                    name="contact_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                  />
                </div>

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="mt-6 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      正在提交
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line" />
                      提交说明
                    </>
                  )}
                </button>

                {status === "success" && (
                  <div className="mt-5 flex items-start gap-3 rounded-md border border-secondary-300 bg-secondary-50 p-4">
                    <i className="ri-checkbox-circle-line mt-0.5 text-lg text-secondary-600" />
                    <p className="text-sm leading-relaxed text-secondary-900">{formMessage}</p>
                  </div>
                )}
                {status === "error" && (
                  <div className="mt-5 flex items-start gap-3 rounded-md border border-primary-300 bg-primary-50 p-4">
                    <i className="ri-error-warning-line mt-0.5 text-lg text-primary-600" />
                    <p className="text-sm leading-relaxed text-primary-900">{formMessage}</p>
                  </div>
                )}
              </form>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                <h2 className="font-heading text-lg font-bold text-foreground-950">其他联系方式</h2>
                <ul className="mt-5 space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-600">
                      <i className="ri-mail-line text-lg" />
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">内容与更正</p>
                      <p className="mt-0.5 break-all text-sm text-foreground-600">{siteConfig.email}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-600">
                      <i className="ri-chat-3-line text-lg" />
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">合作咨询</p>
                      <p className="mt-0.5 break-all text-sm text-foreground-600">{siteConfig.workEmail}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-600">
                      <i className="ri-time-line text-lg" />
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">回复时间</p>
                      <p className="mt-0.5 text-sm text-foreground-600">工作日 10:00 - 18:00，两个工作日内回复</p>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="mt-6 rounded-lg border-l-4 border-accent-500 bg-accent-50 p-6">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-lightbulb-line text-accent-700" />
                  提交小提示
                </h2>
                <ul className="mt-3 space-y-2 text-sm leading-relaxed text-foreground-700">
                  <li className="flex gap-2">
                    <i className="ri-check-line mt-1 text-accent-700" />
                    附上原始来源链接，核实会更高效。
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-check-line mt-1 text-accent-700" />
                    请勿提交未经证实的个人隐私信息。
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-check-line mt-1 text-accent-700" />
                    涉及人身攻击的内容我们不予收录。
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}